// Time conversion and calculation utilities

/**
 * Convert HH:MM to total minutes
 * @param {string} time - Time in HH:MM format
 * @returns {number} Total minutes
 */
export const timeToMinutes = (time) => {
  if (!time) return 0;
  const [hours, minutes] = time.split(':').map(Number);
  return (hours || 0) * 60 + (minutes || 0);
};

/**
 * Convert minutes to HH:MM format
 * @param {number} totalMinutes - Total minutes
 * @returns {string} Time in HH:MM format
 */
export const minutesToTime = (totalMinutes) => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

/**
 * Convert minutes to decimal hours
 * @param {number} totalMinutes - Total minutes
 * @returns {number} Decimal hours (rounded to 2 decimal places)
 */
export const minutesToDecimal = (totalMinutes) => {
  return Number((totalMinutes / 60).toFixed(2));
};

/**
 * Convert decimal hours to minutes
 * @param {number} decimalHours - Decimal hours
 * @returns {number} Total minutes
 */
export const decimalToMinutes = (decimalHours) => {
  return Math.round(decimalHours * 60);
};

/**
 * Validate time input (minutes should be 0-59)
 * @param {number} hours - Hours value
 * @param {number} minutes - Minutes value
 * @returns {object} { valid: boolean, error: string }
 */
export const validateTime = (hours, minutes) => {
  if (minutes < 0 || minutes > 59) {
    return { valid: false, error: 'Minutos devem ser entre 0 e 59' };
  }
  if (hours < 0 || hours > 9999) {
    return { valid: false, error: 'Horas devem ser entre 0 e 9999' };
  }
  return { valid: true, error: '' };
};

/**
 * Sum multiple time entries
 * @param {Array} entries - Array of objects with hours and minutes
 * @returns {object} { hours, minutes, decimal, totalMinutes }
 */
export const sumTimes = (entries) => {
  const totalMinutes = entries.reduce((sum, entry) => {
    return sum + (entry.hours * 60 + entry.minutes);
  }, 0);
  
  return {
    hours: Math.floor(totalMinutes / 60),
    minutes: totalMinutes % 60,
    decimal: minutesToDecimal(totalMinutes),
    totalMinutes
  };
};

/**
 * Subtract two times (HH:MM format)
 * @param {string} timeA - Larger time (HH:MM)
 * @param {string} timeB - Smaller time (HH:MM)
 * @param {boolean} overnight - If true, add 24 hours if result is negative
 * @returns {object} { hours, minutes, decimal, totalMinutes, error }
 */
export const subtractTimes = (timeA, timeB, overnight = false) => {
  let minutesA = timeToMinutes(timeA);
  let minutesB = timeToMinutes(timeB);
  let diff = minutesA - minutesB;
  
  if (diff < 0) {
    if (overnight) {
      diff += 24 * 60; // Add 24 hours
    } else {
      return { 
        hours: 0, 
        minutes: 0, 
        decimal: 0, 
        totalMinutes: 0,
        error: 'Horário final deve ser maior que o inicial' 
      };
    }
  }
  
  return {
    hours: Math.floor(diff / 60),
    minutes: diff % 60,
    decimal: minutesToDecimal(diff),
    totalMinutes: diff,
    error: ''
  };
};

/**
 * Calculate hours worked per day with interval
 * @param {string} entrada - Entry time (HH:MM)
 * @param {string} saida - Exit time (HH:MM)
 * @param {number} intervalo - Interval in minutes
 * @returns {object} { hours, minutes, decimal, totalMinutes }
 */
export const calculateDailyHours = (entrada, saida, intervalo) => {
  let minEntrada = timeToMinutes(entrada);
  let minSaida = timeToMinutes(saida);
  
  // Handle overnight shift
  if (minSaida < minEntrada) {
    minSaida += 24 * 60;
  }
  
  const totalMin = minSaida - minEntrada;
  const liquidMin = totalMin - intervalo;
  
  return {
    hours: Math.floor(liquidMin / 60),
    minutes: liquidMin % 60,
    decimal: minutesToDecimal(liquidMin),
    totalMinutes: liquidMin
  };
};

/**
 * Calculate overtime payment
 * @param {number} valorHoraNormal - Normal hourly rate (R$)
 * @param {number} horasExtras - Extra hours worked
 * @param {number} minutosExtras - Extra minutes worked
 * @param {string} tipoExtra - Type: '50%', '100%', or 'custom'
 * @param {number} customPercent - Custom percentage (if tipoExtra is 'custom')
 * @returns {object} Calculation results
 */
export const calculateOvertime = (valorHoraNormal, horasExtras, minutosExtras, tipoExtra, customPercent = 50) => {
  let multiplier = 1.5; // Default 50%
  
  if (tipoExtra === '100%') {
    multiplier = 2.0;
  } else if (tipoExtra === 'custom') {
    multiplier = 1 + (customPercent / 100);
  }
  
  const valorHoraExtra = valorHoraNormal * multiplier;
  const totalMinutosExtra = horasExtras * 60 + minutosExtras;
  const totalHorasDecimal = totalMinutosExtra / 60;
  const valorTotal = totalHorasDecimal * valorHoraExtra;
  
  return {
    valorHoraExtra: valorHoraExtra.toFixed(2),
    totalHoras: Math.floor(totalMinutosExtra / 60),
    totalMinutos: totalMinutosExtra % 60,
    totalHorasDecimal: totalHorasDecimal.toFixed(2),
    valorTotal: valorTotal.toFixed(2),
    multiplier: multiplier.toFixed(2)
  };
};

/**
 * Calculate night shift premium (adicional noturno)
 * @param {number} valorHoraDiurna - Daytime hourly rate (R$)
 * @param {number} percentualAdicional - Premium percentage (default 20%)
 * @param {number} horasNoturnas - Night hours worked
 * @returns {object} Calculation results
 */
export const calculateNightShift = (valorHoraDiurna, percentualAdicional, horasNoturnas) => {
  // Step 1: Calculate night hourly rate with premium
  const adicionalReais = valorHoraDiurna * (percentualAdicional / 100);
  const valorHoraNoturna = valorHoraDiurna + adicionalReais;
  
  // Step 2: Apply reduced night hour (52.5 min = 1 hour)
  // Conversion factor: 60 / 52.5 = 1.142857
  const fatorReducao = 60 / 52.5;
  const horasReduzidas = horasNoturnas * fatorReducao;
  
  // Step 3: Calculate total payment
  const totalReceber = horasReduzidas * valorHoraNoturna;
  
  // Comparison without night benefits
  const semAdicional = horasNoturnas * valorHoraDiurna;
  const beneficio = totalReceber - semAdicional;
  
  return {
    adicionalReais: adicionalReais.toFixed(2),
    valorHoraNoturna: valorHoraNoturna.toFixed(2),
    horasInformadas: horasNoturnas.toFixed(2),
    horasReduzidas: horasReduzidas.toFixed(2),
    totalReceber: totalReceber.toFixed(2),
    semAdicional: semAdicional.toFixed(2),
    beneficio: beneficio.toFixed(2),
    fatorReducao: fatorReducao.toFixed(6)
  };
};

/**
 * Calculate bank of hours (banco de horas)
 * @param {number} jornadaMinutos - Contracted daily hours in minutes
 * @param {Array} weekData - Array of day objects with entrada, saida, intervalo
 * @returns {object} Calculation results with daily and weekly totals
 */
export const calculateBancoHoras = (jornadaMinutos, weekData) => {
  let totalWorked = 0;
  let daysWorked = 0;
  const dailyResults = [];
  
  weekData.forEach((day) => {
    if (day.entrada && day.saida) {
      let minEntrada = timeToMinutes(day.entrada);
      let minSaida = timeToMinutes(day.saida);
      
      // Handle overnight
      if (minSaida < minEntrada) {
        minSaida += 24 * 60;
      }
      
      const worked = (minSaida - minEntrada) - (day.intervalo || 0);
      const saldoDia = worked - jornadaMinutos;
      
      totalWorked += worked;
      daysWorked++;
      
      dailyResults.push({
        dayName: day.dayName,
        worked,
        workedHours: Math.floor(worked / 60),
        workedMinutes: worked % 60,
        workedDecimal: minutesToDecimal(worked),
        saldoDia,
        saldoHours: Math.floor(Math.abs(saldoDia) / 60),
        saldoMinutes: Math.abs(saldoDia) % 60,
        saldoDecimal: minutesToDecimal(Math.abs(saldoDia)),
        saldoPositive: saldoDia >= 0
      });
    } else {
      dailyResults.push({
        dayName: day.dayName,
        worked: 0,
        workedHours: 0,
        workedMinutes: 0,
        workedDecimal: 0,
        saldoDia: 0,
        saldoHours: 0,
        saldoMinutes: 0,
        saldoDecimal: 0,
        saldoPositive: true
      });
    }
  });
  
  const totalContratada = jornadaMinutos * daysWorked;
  const saldoTotal = totalWorked - totalContratada;
  
  return {
    dailyResults,
    totalWorked,
    totalWorkedHours: Math.floor(totalWorked / 60),
    totalWorkedMinutes: totalWorked % 60,
    totalWorkedDecimal: minutesToDecimal(totalWorked),
    totalContratada,
    totalContratadaHours: Math.floor(totalContratada / 60),
    totalContratadaMinutes: totalContratada % 60,
    totalContratadaDecimal: minutesToDecimal(totalContratada),
    saldoTotal,
    saldoTotalHours: Math.floor(Math.abs(saldoTotal) / 60),
    saldoTotalMinutes: Math.abs(saldoTotal) % 60,
    saldoTotalDecimal: minutesToDecimal(Math.abs(saldoTotal)),
    saldoPositive: saldoTotal >= 0
  };
};

/**
 * Format a number the Brazilian way (comma as decimal separator): 8.5 -> "8,50"
 * @param {number|string} value - Number (or numeric string such as "8.50")
 * @param {number} digits - Decimal places (default 2)
 * @returns {string}
 */
export const formatNumber = (value, digits = 2) => {
  return Number(value).toLocaleString('pt-BR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  });
};

/**
 * Format currency to Brazilian Real
 * @param {number} value - Value to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};